import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Market} from "../../../storage/models/market.model";
import {BehaviorSubject, Observable, Subject, Subscription} from "rxjs";
import {MarketService} from "../../../providers/services/market.service";
import {AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {IGenericKeyValue, IRRUItem} from "../../../storage/interfaces/generic.interface";
import {ReferencesService} from "../../../providers/services/references.service";
import {debounceTime, filter, map, skip, take, takeUntil, tap} from "rxjs/operators";
import {LoaderService} from "../../../providers/services/loader.service";
import {SelectOption} from "@df/design-system-components";
import {IQuestionnaire, IQuestionnaireForApi} from "../../../storage/interfaces/questionnaire.interface";
import {QuestionnaireService} from "../../../providers/services/questionnaire.service";
import {IApiResponse} from "../../../storage/interfaces/api.interface";
import {
  CONSTANTS,
  DMDS_QUANTITY_UNIT_LIST,
  VALIDATOR_MAX_DMDS_QTY,
  VALIDATOR_MIN_DMDS_QTY,
  CATALYST_WEIGHT_UNIT_LIST,
  PRODUCT_FAMILY,
  VALIDATOR_MIN_CATALYST_WEIGHT,
  VALIDATOR_MAX_CATALYST_WEIGHT,
} from "../../../constants";
import {GenericKeyValueFormGroup} from "../../../storage/models/generic-key-value-form-group";
import {DateService} from "../../../providers/services/date.service";
import {MissionsService} from "../../../providers/services/missions.service";
import {ToastService} from "../../../providers/services/toast.service";
import {controlErrors, IFieldError} from "../../../providers/utils/form-errors";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {ProductsService} from "../../../providers/services/products.service";
import {IProduct} from "../../../storage/interfaces/product.interface";

@Component({
    selector: 'app-mission-create',
    templateUrl: './mission-create.component.html',
    styleUrls: ['./mission-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MissionCreateComponent implements OnInit, OnDestroy {
  selectedMarket: Market;
  selectedMarketSubscription: Subscription;

  form!: UntypedFormGroup;
  rruFC = new UntypedFormControl(null, Validators.required);
  refineryCorporationFC = new UntypedFormControl(null, Validators.required);
  refineryFC = new UntypedFormControl({value: null, disabled: true}, Validators.required);
  unitFC = new UntypedFormControl({value: null, disabled: true}, Validators.required);
  injectionRows!: UntypedFormArray;
  sulfidingDateFC = new UntypedFormControl('', Validators.required);
  questionnaireIdFC = new UntypedFormControl('', Validators.required);

  refineryCorporationOptions: SelectOption<number>[] = [];
  refineryOptions: SelectOption<number>[] = [];
  unitOptions: SelectOption<number>[] = [];
  locationOptions: SelectOption<number>[] = [];
  trainOptions: SelectOption<number>[] = [];

  private _rruListSubject = new BehaviorSubject<IRRUItem[]>([]);
  private _questionnaireDataListSubject = new BehaviorSubject<IQuestionnaire<string>[]>([]);
  questionnaireDataList$ = this._questionnaireDataListSubject.asObservable();
  questionnaireOptions$: Observable<SelectOption<number>[]> = this.questionnaireDataList$.pipe(
    map(list => [
      ...list.map(questionnaire => this._toQuestionnaireOption(questionnaire)),
      {value: -1, label: 'Create manually', description: 'Manual creation without reference to a questionnaire', icon: 'heroWrench'}
    ])
  );
  private _selectedDataSubject = new BehaviorSubject<IQuestionnaire<string>>({} as IQuestionnaire<string>);
  selectedData$ = this._selectedDataSubject.asObservable();
  private _componentDestroyed$: Subject<boolean> = new Subject();
  private _productListSubject = new BehaviorSubject<IProduct[]>([]);
  productList$ = this._productListSubject.asObservable();
  productOptions$: Observable<SelectOption<number>[]> = this.productList$.pipe(
    map(list => list.map(product => ({value: product.productpk, label: product.productName})))
  );
  private _creatingSubject = new BehaviorSubject<boolean>(false);
  creating$= this._creatingSubject.asObservable();

  VALIDATOR_MIN_DMDS_QTY = VALIDATOR_MIN_DMDS_QTY;
  VALIDATOR_MAX_DMDS_QTY = VALIDATOR_MAX_DMDS_QTY;
  DMDS_QUANTITY_UNIT_LIST = DMDS_QUANTITY_UNIT_LIST;
  CATALYST_WEIGHT_UNIT_LIST = CATALYST_WEIGHT_UNIT_LIST;
  VALIDATOR_MIN_CATALYST_WEIGHT = VALIDATOR_MIN_CATALYST_WEIGHT;
  VALIDATOR_MAX_CATALYST_WEIGHT = VALIDATOR_MAX_CATALYST_WEIGHT;
  constructor(private _marketService: MarketService,
              private _loaderService: LoaderService,
              private _referenceService: ReferencesService,
              private _questionnaireService: QuestionnaireService,
              private _fb: UntypedFormBuilder,
              private _dateService: DateService,
              private _missionService: MissionsService,
              private _toastService: ToastService,
              private _router: Router,
              private _translateService: TranslateService,
              private _productsService: ProductsService
  ) {
  }

  ngOnInit(): void {
    this._loaderService.show();
    this.questionnaireIdFC.disable({emitEvent: false});

    this._productsService.getProducts(false)
      .then(products => this._productListSubject.next(products ?? []))
      .catch(error => console.error('Failed to load products for mission creation', error));

    this.form = this._fb.group({
      rru: this.rruFC,
      refineryCorporation: this.refineryCorporationFC,
      refinery: this.refineryFC,
      unit: this.unitFC,
      questionnaireId: this.questionnaireIdFC,
      injections: this._fb.array([], this._uniqueTrainProductValidator),
      sulfidingDate: this.sulfidingDateFC
    });
    this.injectionRows = this.form.get('injections') as UntypedFormArray;
    this.addInjectionRow();

    this.refineryCorporationFC.valueChanges.pipe(takeUntil(this._componentDestroyed$))
      .subscribe(corporationId => this._onCorporationSelected(corporationId));
    this.refineryFC.valueChanges.pipe(takeUntil(this._componentDestroyed$))
      .subscribe(refineryId => this._onRefinerySelected(refineryId));
    this.unitFC.valueChanges.pipe(takeUntil(this._componentDestroyed$))
      .subscribe(unitId => this._onUnitSelected(unitId));
    this.selectedMarketSubscription = this._marketService.selectedMarket$.subscribe(async selectedMarket => {
      if (selectedMarket) {
        this.form.reset();
        this.selectedMarket = selectedMarket;
        this._updateCorporationOptions();
      }
    });

    this._referenceService.getRRUListByMarket()
      .pipe(
        take(1),
        tap(() => this._loaderService.hide()))
      .subscribe(resp => {
        this._rruListSubject.next(resp.data);
        this._updateCorporationOptions();
      })

    this.rruFC.valueChanges
      .pipe(takeUntil(this._componentDestroyed$),
        debounceTime(250),
      )
      .subscribe(v => {
        const unitId = v == null || v === '' ? null : Number(v);
        this._onLocationSelected(unitId != null && Number.isFinite(unitId) ? unitId : null);
        if (unitId != null && Number.isFinite(unitId)) {
          this._loadQuestionnairesList();
          this.questionnaireIdFC.enable({emitEvent: true});
        } else {
          this.questionnaireIdFC.disable({emitEvent: true});
        }
        this._clearSelectedData();
      });

    this.questionnaireIdFC.valueChanges
      .pipe(takeUntil(this._componentDestroyed$),
        skip(1),
      )
      .subscribe((v) => {
        let newValue: IQuestionnaire<string> = {} as IQuestionnaire<string>;
        if (!v) {
          newValue = null;
        } else if (v === -1) {
          newValue = {
            preparationPk: -1,
            unitId: this.rruFC.value,
            reactors: [{name: 'Reactor 1', bedNumber: 1}]
          } as IQuestionnaire<string>;
        } else {
          const liste = this._questionnaireDataListSubject.getValue();
          const foundItem = liste.find(i => i.preparationPk === v);
          newValue = !!foundItem ? foundItem : {} as IQuestionnaire<string>;
        }
        this._selectedDataSubject.next(newValue);
        this._propagateData();
      });
  }

  ngOnDestroy(): void {
    this._componentDestroyed$.next(void 0);
  }

  compare(o1: IGenericKeyValue, o2: IGenericKeyValue): boolean {
    return (o1 == o2);
  }

  productErrors(index: number): IFieldError[] {
    return controlErrors(this.injectionRows.at(index).get('productPk'), {required: 'Please select a product'});
  }

  requiresCatalystWeight(index: number): boolean {
    const productpk = Number(this.injectionRows.at(index).get('productPk')?.value);
    return this._productListSubject.getValue().some(product => product.productpk === productpk
      && product.productFamilyPk === PRODUCT_FAMILY.SULFUR_PRODUCTS);
  }

  productOptionsForRow(index: number, options: SelectOption<number>[]): SelectOption<number>[] {
    const row = this.injectionRows.at(index);
    const trainpk = this._normalizeTrainpk(row.get('trainpk')?.value);
    if (trainpk == null) { return options; }

    const productsUsedByTrain = new Set<number>();
    this.injectionRows.controls.forEach((otherRow, otherIndex) => {
      if (otherIndex === index || this._normalizeTrainpk(otherRow.get('trainpk')?.value) !== trainpk) { return; }
      const productpk = Number(otherRow.get('productPk')?.value);
      if (Number.isFinite(productpk) && productpk > 0) { productsUsedByTrain.add(productpk); }
    });

    return options.filter(option => !productsUsedByTrain.has(Number(option.value)));
  }

  private _normalizeTrainpk(value: unknown): number | null {
    if (value == null || value === '') { return null; }
    const trainpk = Number(value);
    return Number.isFinite(trainpk) ? trainpk : null;
  }

  sulfidingDateErrors(): IFieldError[] {
    return controlErrors(this.sulfidingDateFC, {required: this._translateService.instant('REQUIRED_FIELD')});
  }

  onValidateNewMissionClickHandler(): void {
    this._creatingSubject.next(true);
    const selectedData = this._selectedDataSubject.getValue();
    const {nbReactor, reactors, ...missionData} = selectedData;
    const injections = this.injectionRows.controls.map(row => ({
      trainpk: Number(row.get('trainpk')?.value) || null,
      productPk: Number(row.get('productPk')?.value),
      quantity: Number(row.get('quantity')?.value),
      quantityUnit: row.get('quantityUnit')?.get('key')?.value,
      catalystWeight: row.get('catalystWeight')?.value === '' || row.get('catalystWeight')?.value == null
        ? null
        : Number(row.get('catalystWeight')?.value),
      catalystWeightUnit: row.get('catalystWeightUnit')?.get('key')?.value ?? null
    }));
    const firstInjection = injections[0];
    const newMission: IQuestionnaireForApi = {
      ...missionData,
      productPk: firstInjection.productPk,
      quantity: firstInjection.quantity,
      quantityUnit: firstInjection.quantityUnit,
      // df-date-field gives 'YYYY-MM-DD': read it as local midnight, like the former datepicker
      sulfidingDate: this._dateService.convertDateToTimestamp(`${this.sulfidingDateFC.value}T00:00:00`),
      market: this.selectedMarket.marketname,
      injections: injections.length ? injections : undefined,
    };

    if (newMission.totalCatalystQuantity == null) newMission.totalCatalystQuantity = 0;
    if (!newMission.totalCatalystQuantityUnit) newMission.totalCatalystQuantityUnit = 'TO';

    this._missionService.createNewMission(newMission)
      .pipe(take(1))
      .subscribe({
      next: (res: IApiResponse<boolean>) => {
        if (res.data) {
          this._toastService.show('Mission has been successfully created');
          this._router.navigateByUrl(`${CONSTANTS.ROUTES.HOME}/${CONSTANTS.ROUTES.MISSION_SELECTION}`);
        } else {
          this._toastService.show('Error while creating mission');
        }
        this._creatingSubject.next(false);
      },
      error: (err) => {
        this._toastService.show('Error while creating mission');
        console.error(err);
        this._creatingSubject.next(false);
      }});
  }

  private _availableRruItems(): IRRUItem[] {
    if (!this.selectedMarket?.marketname) { return []; }
    return this._rruListSubject.getValue()
      .filter(item => item.market === this.selectedMarket.marketname);
  }

  private _updateCorporationOptions(): void {
    const locations = new Map<number, IRRUItem>();
    this._availableRruItems().forEach(item => {
      if (!locations.has(item.unitId)) { locations.set(item.unitId, item); }
    });
    this.locationOptions = [...locations.values()].map(item => ({
      value: item.unitId,
      label: `${item.refineryCorpName || item.refineryCorpShortName} / ${item.refineryFullName || item.refineryShortName} / ${item.unitName}`
    }));

    const corporations = new Map<number, string>();
    this._availableRruItems().forEach(item => {
      corporations.set(item.refineryCorpId, item.refineryCorpName || item.refineryCorpShortName);
    });

    this.refineryCorporationOptions = [...corporations.entries()]
      .map(([value, label]) => ({value, label}));
  }

  private _onCorporationSelected(corporationId: number | null): void {
    this.refineryFC.reset(null, {emitEvent: false});
    this.unitFC.reset(null, {emitEvent: false});
    this.refineryFC.disable({emitEvent: false});
    this.unitFC.disable({emitEvent: false});
    this.unitOptions = [];
    this.trainOptions = [];
    this._resetInjectionRows();
    this.rruFC.setValue(null);

    const refineries = new Map<number, string>();
    if (corporationId != null) {
      this._availableRruItems()
        .filter(item => item.refineryCorpId === Number(corporationId))
        .forEach(item => refineries.set(item.refineryId, item.refineryFullName || item.refineryShortName));
    }
    this.refineryOptions = [...refineries.entries()].map(([value, label]) => ({value, label}));
    if (this.refineryOptions.length) { this.refineryFC.enable({emitEvent: false}); }
  }

  private _onRefinerySelected(refineryId: number | null): void {
    this.unitFC.reset(null, {emitEvent: false});
    this.unitFC.disable({emitEvent: false});
    this.trainOptions = [];
    this._resetInjectionRows();
    this.rruFC.setValue(null);

    const units = new Map<number, string>();
    if (refineryId != null) {
      this._availableRruItems()
        .filter(item => item.refineryCorpId === Number(this.refineryCorporationFC.value)
          && item.refineryId === Number(refineryId))
        .forEach(item => units.set(item.unitId, item.unitName));
    }
    this.unitOptions = [...units.entries()].map(([value, label]) => ({value, label}));
    if (this.unitOptions.length) { this.unitFC.enable({emitEvent: false}); }
  }

  private _onUnitSelected(unitId: number | null): void {
    this.rruFC.setValue(unitId == null ? null : Number(unitId));
  }

  private _onLocationSelected(unitId: number | null): void {
    if (unitId == null) {
      this.trainOptions = [];
      this._resetInjectionRows();
      return;
    }

    const unitItem = this._availableRruItems().find(item => item.unitId === unitId);
    if (!unitItem) { return; }

    this.refineryCorporationFC.setValue(unitItem.refineryCorpId, {emitEvent: false});
    this.refineryFC.setValue(unitItem.refineryId, {emitEvent: false});
    this.unitFC.setValue(unitItem.unitId, {emitEvent: false});
    this.refineryFC.enable({emitEvent: false});
    this.unitFC.enable({emitEvent: false});

    const refineries = new Map<number, string>();
    const units = new Map<number, string>();
    this._availableRruItems().forEach(item => {
      if (item.refineryCorpId === unitItem.refineryCorpId) {
        refineries.set(item.refineryId, item.refineryFullName || item.refineryShortName);
      }
      if (item.refineryCorpId === unitItem.refineryCorpId && item.refineryId === unitItem.refineryId) {
        units.set(item.unitId, item.unitName);
      }
    });
    this.refineryOptions = [...refineries.entries()].map(([value, label]) => ({value, label}));
    this.unitOptions = [...units.entries()].map(([value, label]) => ({value, label}));

    const unitItems = this._availableRruItems().filter(item => item.unitId === unitId);
    this.trainOptions = unitItems.map(item => ({
      value: item.trainpk ?? 0,
      label: item.trainId === 'DEFAULT'
        ? this._translateService.instant('UI.NEW_MISSION.DEFAULT_TRAIN')
        : item.trainName || item.trainId
    }));

    this._resetInjectionRows(this.trainOptions[0]?.value ?? null);
  }

  addInjectionRow(defaultTrainpk: number | null = null): void {
    if (!this.injectionRows) { return; }
    const previousTrainpk = this.injectionRows.length
      ? this.injectionRows.at(this.injectionRows.length - 1).get('trainpk')?.value
      : null;
    const row = this._fb.group({
      trainpk: [defaultTrainpk ?? previousTrainpk ?? null, Validators.required],
      productPk: [null, Validators.required],
      quantity: ['', [Validators.required, Validators.min(VALIDATOR_MIN_DMDS_QTY), Validators.max(VALIDATOR_MAX_DMDS_QTY)]],
      quantityUnit: new GenericKeyValueFormGroup({key: 'TO', value: 'MT'}),
      catalystWeight: [''],
      catalystWeightUnit: new GenericKeyValueFormGroup({key: 'TO', value: 'MT'})
    });
    this.injectionRows.push(row);
    row.get('productPk')?.valueChanges.pipe(takeUntil(this._componentDestroyed$))
      .subscribe(productpk => this._setCatalystWeightValidation(row, productpk));
  }

  private _setCatalystWeightValidation(row: AbstractControl, productpk: number | null): void {
    const catalystWeight = row.get('catalystWeight');
    if (!catalystWeight) { return; }

    const isSulfurProduct = this._productListSubject.getValue().some(product => product.productpk === Number(productpk)
      && product.productFamilyPk === PRODUCT_FAMILY.SULFUR_PRODUCTS);
    if (isSulfurProduct) {
      catalystWeight.setValidators([
        Validators.required,
        Validators.min(VALIDATOR_MIN_CATALYST_WEIGHT),
        Validators.max(VALIDATOR_MAX_CATALYST_WEIGHT)
      ]);
    } else {
      catalystWeight.clearValidators();
      catalystWeight.setValue(null, {emitEvent: false});
    }
    catalystWeight.updateValueAndValidity({emitEvent: false});
  }

  private _uniqueTrainProductValidator(control: AbstractControl): {[key: string]: boolean} | null {
    const rows = (control as UntypedFormArray).controls;
    const combinations = rows
      .map(row => `${row.get('trainpk')?.value ?? ''}:${row.get('productPk')?.value ?? ''}`)
      .filter(value => !value.endsWith(':'));
    return new Set(combinations).size === combinations.length ? null : {duplicateTrainProduct: true};
  }

  removeInjectionRow(index: number): void {
    if (this.injectionRows.length > 1) { this.injectionRows.removeAt(index); }
  }

  private _resetInjectionRows(defaultTrainpk: number | null = null): void {
    if (!this.injectionRows) { return; }
    this.injectionRows.clear();
    this.addInjectionRow(defaultTrainpk);
  }

  private _toQuestionnaireOption(questionnaire: IQuestionnaire<string>): SelectOption<number> {
    const sulfidingDate = !!questionnaire.sulfidingDate
      ? this._dateService.convertTimestampToDate(questionnaire.sulfidingDate, 'EMEA')
      : ' - ';
    return {
      value: questionnaire.preparationPk,
      label: `Sulfiding date: ${sulfidingDate}`,
      description: `Questionnaire status: ${questionnaire.status?.value ?? '-'} · Mission status: ${questionnaire.missionStatus?.value ?? '-'}`,
      disabled: questionnaire.disabled
    };
  }

  private _loadQuestionnairesList(): void {
    const unitId = Number(this.rruFC.value);
    // check if rru exists in the list to avoid URL Redirection to Untrusted Site ('Open Redirect') flaw (CWE 601)
    const rruList = this._rruListSubject.getValue();
    const foundRru = rruList.some(r => r.unitId === unitId && r.market === this.selectedMarket?.marketname);

    if (Number.isFinite(unitId) && unitId > 0 && foundRru) {
      this._questionnaireService.getQuestionnairesByRRU(unitId)
        .pipe(
          take(1),
          filter((apiResponse: IApiResponse<IQuestionnaire<string>[]>) => apiResponse.success),
          map((apiResponse: IApiResponse<IQuestionnaire<string>[]>) => apiResponse.data)
        )
        .subscribe((list: IQuestionnaire<string>[]) => {
          this._questionnaireDataListSubject.next(list);
        });
    }
  }

  // df-date-field works with 'YYYY-MM-DD' strings (local date)
  private _toDateFieldValue(date: Date): string {
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private _clearSelectedData(): void {
    this.questionnaireIdFC.setValue(null);
  }

  private _propagateData(): void {
    const selectedQuestionnaire = this._selectedDataSubject.getValue();
    if (!!selectedQuestionnaire) {

      if (!selectedQuestionnaire?.preparationPk || selectedQuestionnaire.preparationPk === -1) {
        const firstInjection = this.injectionRows.at(0);
        firstInjection?.get('productPk')?.setValue(null);
        firstInjection?.get('quantity')?.setValue('');
        (firstInjection?.get('quantityUnit') as GenericKeyValueFormGroup)?.setValue({key: 'TO', value: 'MT'});
        firstInjection?.get('catalystWeight')?.setValue(null);
        (firstInjection?.get('catalystWeightUnit') as GenericKeyValueFormGroup)?.setValue({key: 'TO', value: 'MT'});
        this.sulfidingDateFC.setValue('');

      } else {
        const firstInjection = this.injectionRows.at(0);
        firstInjection?.get('productPk')?.setValue(selectedQuestionnaire.productPk, {emitEvent: true});
        firstInjection?.get('quantity')?.setValue(selectedQuestionnaire.quantity, {emitEvent: true});
        (firstInjection?.get('quantityUnit') as GenericKeyValueFormGroup)?.setValue({key: selectedQuestionnaire.quantityUnit, value: ''}, {emitEvent: true});
        firstInjection?.get('catalystWeight')?.setValue(selectedQuestionnaire.totalCatalystQuantity ?? null, {emitEvent: true});
        (firstInjection?.get('catalystWeightUnit') as GenericKeyValueFormGroup)?.setValue({
          key: selectedQuestionnaire.totalCatalystQuantityUnit || 'TO', value: ''
        }, {emitEvent: true});
        if (!!selectedQuestionnaire.sulfidingDate) {
          const sulfidingDate = this._toDateFieldValue(new Date(parseInt(selectedQuestionnaire.sulfidingDate, 10) * 1000));
          this.sulfidingDateFC.setValue(sulfidingDate, {emitEvent: true});
        } else {
          this.sulfidingDateFC.setValue('', {emitEvent: true});
        }
      }
    }
  }
}
